/**
 * Canonical “last online” for cart nudge automation and reporting.
 * Writes to Firestore `user_last_active/{uid}` (throttled) — see AI_AGENT_SERVICE_GUIDE.
 */

import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export const USER_LAST_ACTIVE_COLLECTION = 'user_last_active';

/**
 * How often the client tries to refresh presence while the tab is visible.
 * Default 10s — override with `REACT_APP_LAST_ACTIVE_HEARTBEAT_INTERVAL_MS`.
 */
export const LAST_ACTIVE_HEARTBEAT_INTERVAL_MS =
    Number(process.env.REACT_APP_LAST_ACTIVE_HEARTBEAT_INTERVAL_MS) > 0
        ? Number(process.env.REACT_APP_LAST_ACTIVE_HEARTBEAT_INTERVAL_MS)
        : 10 * 1000;

/**
 * Minimum time between Firestore writes per uid (throttle). Defaults to the heartbeat interval.
 * Override with `REACT_APP_LAST_ACTIVE_MIN_WRITE_INTERVAL_MS` (e.g. raise to reduce write volume).
 */
export const LAST_ACTIVE_MIN_WRITE_INTERVAL_MS =
    Number(process.env.REACT_APP_LAST_ACTIVE_MIN_WRITE_INTERVAL_MS) > 0
        ? Number(process.env.REACT_APP_LAST_ACTIVE_MIN_WRITE_INTERVAL_MS)
        : LAST_ACTIVE_HEARTBEAT_INTERVAL_MS;

const lastWriteAtByUid = Object.create(null);

/**
 * Record that the signed-in user had the app open (foreground / heartbeat).
 * Throttled per uid unless `options.force` (session start, tab focus, page hide).
 * @param {string} [uid]
 * @param {{ force?: boolean }} [options]
 */
export function recordUserLastActive(uid, options = {}) {
    if (!uid) return;
    const now = Date.now();
    const prev = lastWriteAtByUid[uid] || 0;
    const force = options.force === true;
    if (!force && now - prev < LAST_ACTIVE_MIN_WRITE_INTERVAL_MS) return;
    lastWriteAtByUid[uid] = now;

    setDoc(
        doc(db, USER_LAST_ACTIVE_COLLECTION, uid),
        {
            lastActiveAt: serverTimestamp(),
            lastActiveAtSource: 'heartbeat',
        },
        { merge: true }
    ).catch(() => {});
}
